import React, { useEffect, useRef, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import CommonImage from '@components/CommonImage';
import LoaderComponent from '@components/LoaderComponent';
import { toastError, toastSuccess } from '@components/Toast';

import useClickOutside from '@hooks/useClickOutside';

import MaterialRequisitionModalWrapper from '@features/material-requisition/components/MaterialRequisitionModalWrapper';
import {
	useCompleteMaterialRequisitionByIdMutation,
	useFetchMaterialRequisitionFilterOptionsMutation,
	useForceApproveByRequisitionByIdMutation,
	useLazyFetchMaterialRequisitionsQuery,
	useMarkAsFreeByRequisitionIdMutation
} from '@features/material-requisition/api/materialRequisitionApi';
import {
	isMaterialTeam,
	isShowMaterialRequisitionBtn,
	isStoreManager,
	mapFabricTypeList,
	MATERIAL_REQUISITION_PURPOSE,
	MATERIAL_REQUISITION_TYPE,
	renderColorsColumn
} from '@features/material-requisition/utils';
import MaterialRejectionModal from '@features/material-requisition/components/MaterialRejectionModal';
import ViewMaterialRequisition from '@features/material-requisition/components/ViewMaterialRequisition';
import {
	allFilterPages,
	materialRequisitionFilterFieldList,
	materialRequisitionTrimsAndAccessoriesFilterFieldList
} from '@features/filter/static/filterUtil';
import FilterComponentV2 from '@features/filter/v2/FilterComponent';
import MaterialRequisitionSummary from '@features/material-requisition/components/MaterialRequisitionSummary';
import Chip from '@features/filter/Chip';
import useMaterialRequisitionFilterParams from '@features/material-requisition/hooks/useMaterialRequisitionFilterParams';
import UpdateInventory from '@features/material-requisition/components/UpdateInventory';
import Search from '@features/filter/Common/Search';
import ViewInventory from '@features/material-requisition/components/ViewInventory';
import { useMaterialRequisition } from '@features/material-requisition/redux/reducer/MaterialRequisitionReducer';

import { Types } from '@store/types';

import { downloadRangePlan } from '@services/rangePlanService';

import { capitalizeFirstLetter, changeDateFormat, getShortName, isPageReachBottom } from '@utils/Util';

const MaterialRequisition = () => {
	const [isShowMaterialRequestTypeModal, setIsShowMaterialRequestTypeModal] = useState(false);
	const [fetchMaterialRequisitions, { data, isLoading }] = useLazyFetchMaterialRequisitionsQuery();
	const [tabState, setTabState] = useState({ page: null, activeTab: null });
	const { filterRequestBody } = useMaterialRequisitionFilterParams({ requisitionType: tabState.activeTab });
	const [fetchMaterialRequisitionFilterOptions, { data: filterOptions }] =
		useFetchMaterialRequisitionFilterOptionsMutation();
	const [completeMaterialRequisitionById, { isLoading: isMarkAsCompletedLoading }] =
		useCompleteMaterialRequisitionByIdMutation();
	const [forceApproveByRequisitionById, { isLoading: isForceApproveLoading }] =
		useForceApproveByRequisitionByIdMutation();
	const [markAsFreeByRequisitionId, { isLoading: isFreeRequisitionLoading }] =
		useMarkAsFreeByRequisitionIdMutation();
	const [isShowRejectionModal, setIsShowRejectionModal] = useState(false);
	const [isShowViewModal, setIsShowViewModal] = useState(false);
	const [isShowInventoryModal, setIsShowInventoryModal] = useState(false);
	const [selectedRequisition, setSelectedRequisition] = useState(null);
	const dispatch = useDispatch();
	const [isShowFilter, setIsShowFilter] = useState(false);
	const [isShowUpdateInventory, setIsShowUpdateInventory] = useState(false);
	const materials = useMaterialRequisition();
	const [searchParams, setSearchParams] = useSearchParams();
	const [selectedApproval, setSelectedApproval] = useState(null);
	const approvalRef = useRef(null);

	const handleClose = () => {
		setSelectedApproval(null);
	};

	useClickOutside(approvalRef, handleClose);

	useEffect(() => {
		const tabParam = searchParams.get('activeTab');
		if (tabParam && MATERIAL_REQUISITION_TYPE.some((item) => item.constant === tabParam)) {
			setTabState({ activeTab: tabParam, page: 0 });
		}
	}, [searchParams]);

	useEffect(() => {
		if (!filterOptions) return;

		const filterList = {
			...filterOptions,
			brandList: filterOptions?.brandList || [],
			createdByList: filterOptions?.requestorList || [],
			statusList:
				filterOptions?.statusList?.map((item) => ({
					...item,
					name: item.name.replaceAll('_', ' ')
				})) || [],
			fabricTypeList: mapFabricTypeList(filterOptions?.fabricConstructoinGroupList)
		};

		dispatch({
			type: Types.STORE_DEPENDENT_FILTER_LIST,
			payload: { responseData: filterList, filterListPage: allFilterPages.MATERIAL_REQUISITION }
		});
	}, [filterOptions]);

	useEffect(() => {
		document.addEventListener('scroll', handleScroll);
		return () => {
			document.removeEventListener('scroll', handleScroll);
		};
	}, [tabState, data, isLoading]);

	const handleTabChange = (newTab) => {
		setSearchParams({ activeTab: newTab });
	};

	const handleScroll = useDebouncedCallback(async () => {
		if (isPageReachBottom() && !isLoading && data?.currentPage + 1 < data?.totalPages) {
			setTabState((prev) => ({ ...prev, page: prev.page + 1 }));
			await getMaterialRequisitionList(tabState.page + 1);
		}
	}, 100);

	const toggleRequestTypeModal = () => {
		setIsShowMaterialRequestTypeModal((prev) => !prev);
	};

	const toggleRejectionModal = () => {
		setIsShowRejectionModal((prev) => !prev);
	};

	const toggleViewModal = () => {
		setIsShowViewModal((prev) => !prev);
	};

	const toggleUpdateModal = () => {
		setIsShowUpdateInventory((prev) => !prev);
	};

	const toggleInventoryModal = () => {
		setIsShowInventoryModal((prev) => !prev);
	};

	useEffect(() => {
		if (isShowFilter && filterRequestBody?.requisitionType) {
			fetchMaterialRequisitionFilterOptions({ requestBody: filterRequestBody });
		}
	}, [isShowFilter, filterRequestBody]);

	const getMaterialRequisitionList = async (pageNumber = tabState.page) => {
		if (filterRequestBody?.requisitionType && pageNumber !== null) {
			try {
				await fetchMaterialRequisitions({
					params: `?page=${pageNumber}&size=20&sort=id,desc`,
					requestBody: filterRequestBody
				}).unwrap();
			} catch {
				toastError('Failed to fetch material requisition list');
			}
		}
	};

	useEffect(() => {
		if (tabState.page !== null) {
			(async () => {
				await getMaterialRequisitionList(0);
				setTabState((prev) => ({ ...prev, page: 0 }));
			})();
		}
	}, [filterRequestBody]);

	const renderRangePlan = (item) => {
		let entityId = item?.collectionResponse?.id || item?.favouriteFolderResponse?.id;
		let entityType = item?.collectionResponse ? 'COLLECTION' : 'FAVOURITE_FOLDER';
		return (
			<span
				className="blue regular-14 text-underline cursor-pointer mr-2 d-block"
				onClick={() => downloadRangePlan(entityId, entityType)}
			>
				Range Plan
			</span>
		);
	};

	const renderDocuments = (item) => {
		return item?.documentResponseList?.map((doc) => (
			<span
				className="blue regular-14 text-underline cursor-pointer mr-2"
				key={doc.id}
				onClick={() => window.open(doc.docUrl, '_blank')}
			>
				{getShortName(doc.name, 15)}
			</span>
		));
	};

	const markAsCompleted = async (requisitionId) => {
		try {
			await completeMaterialRequisitionById({ requisitionId }).unwrap();
			toastSuccess('Material requisition marked as completed successfully');
		} catch {
			toastError('Failed to mark material requisition as completed');
		}
	};

	const markAsFree = async (requisitionId) => {
		try {
			await markAsFreeByRequisitionId({ requisitionId }).unwrap();
			toastSuccess('Material requisition approved successfully');
		} catch {
			toastError('Failed to approve material requisition');
		}
	};

	const approveRequisition = async (requisitionId) => {
		try {
			await forceApproveByRequisitionById({ requisitionId }).unwrap();
			toastSuccess('Material requisition approved successfully');
		} catch {
			toastError('Failed to approve material requisition');
		}
	};

	const renderActionButton = (material) => {
		const actions = [];

		const addAction = (label, onClick) => (
			<li role="presentation" className="px-4 pb-3 pt-3 w-100">
				<span
					role="menuitem"
					tabIndex={-1}
					className="font-weight-normal text-black"
					onClick={onClick}
				>
					{label}
				</span>
			</li>
		);

		switch (material.status) {
			case 'MATERIAL_ADDED':
				actions.push(
					addAction('View Details', () => {
						setSelectedRequisition(material);
						toggleViewModal();
					})
				);
				if (isMaterialTeam()) {
					actions.push(
						addAction('Send to inventory', () => approveRequisition(material.id)),
						addAction('Reject', () => {
							setSelectedRequisition(material);
							toggleRejectionModal();
						})
					);
					if (
						material.requisitionType !== MATERIAL_REQUISITION_TYPE[1].constant &&
						!material?.isFreeOfCost
					) {
						actions.push(
							addAction('Mark as free of Cost', async () => {
								await markAsFree(material.id);
							})
						);
					}
				}
				break;
			case 'REQUESTED':
				actions.push(
					addAction('View Details', () => {
						setSelectedRequisition(material);
						toggleViewModal();
					})
				);
				if (isMaterialTeam()) {
					actions.push(
						addAction('Reject', () => {
							setSelectedRequisition(material);
							toggleRejectionModal();
						})
					);
					if (
						material.requisitionType !== MATERIAL_REQUISITION_TYPE[1].constant &&
						!material?.isFreeOfCost
					) {
						actions.push(
							addAction('Mark as free of Cost', async () => {
								await markAsFree(material.id);
							})
						);
					}
				}
				break;
			case 'SENT_FOR_APPROVAL':
				actions.push(
					addAction('View Details', () => {
						setSelectedRequisition(material);
						toggleViewModal();
					})
				);
				if (isMaterialTeam()) {
					actions.push(
						addAction('Force Approve', async () => {
							await approveRequisition(material.id);
						})
					);
				}
				break;
			case 'APPROVED':
				if (isStoreManager()) {
					actions.push(
						addAction('Update Inventory', () => {
							setSelectedRequisition(material);
							toggleUpdateModal();
						})
					);
				}
				break;
			case 'INVENTORY_ADDED':
				if (isStoreManager()) {
					actions.push(
						addAction('Update Inventory', () => {
							setSelectedRequisition(material);
							toggleUpdateModal();
						})
					);
				}
				actions.push(
					addAction('View Inventory', () => {
						setSelectedRequisition(material);
						toggleInventoryModal();
					})
				);
				if (isMaterialTeam()) {
					actions.push(addAction('Mark as Completed', () => markAsCompleted(material.id)));
				}
				break;
			case 'COMPLETED':
				actions.push(
					addAction('View Inventory', () => {
						setSelectedRequisition(material);
						toggleInventoryModal();
					})
				);
				break;
			case 'DISBURSEMENT_PENDING':
				if (isMaterialTeam()) {
					actions.push(
						addAction('Force Approve', async () => {
							await approveRequisition(material.id);
						})
					);
				}
				break;
			default:
				break;
		}

		if (material?.collectionResponse?.id) {
			actions.push(
				addAction(
					'Go to Collection',
					() => {
						window.open(`/collections/view/${material.collectionResponse.id}`, '_blank');
					},
					'goToCollection'
				)
			);
		}
		if (material?.favouriteFolderResponse?.id) {
			actions.push(
				addAction(
					'Go to Folder',
					() => {
						window.open(`/favourite/${material.favouriteFolderResponse.id}`, '_blank');
					},
					'goToFolder'
				)
			);
		}

		if (actions.length === 0) return null;

		return actions;
	};

	const renderStatusWiseActions = (item) => {
		return (
			<>
				{item?.isFreeOfCost === true &&
					isStoreManager() &&
					['REQUESTED', 'MATERIAL_ADDED'].includes(item.status) && (
						<button
							className="button text size32 pr-1 pl-0 gray_dark_02 d-flex align-items-center"
							onClick={() => {
								setSelectedRequisition(item);
								toggleUpdateModal();
							}}
						>
							<p className="regular-14 blue text-underline">Update Inventory</p>
							<CommonImage src="/icons/right-arrow.svg" width={18} height={18} />
						</button>
					)}
				{item?.isFreeOfCost !== true &&
					isMaterialTeam() &&
					item.status === 'REQUESTED' &&
					item.requisitionType !== MATERIAL_REQUISITION_TYPE[1].constant && (
						<button
							className="button text size32 pr-1 pl-0 gray_dark_02 d-flex align-items-center"
							onClick={() => {
								setSelectedRequisition(item);
								setIsShowViewModal(true);
							}}
						>
							<p className="regular-14 blue text-underline">
								{[
									MATERIAL_REQUISITION_PURPOSE[0].constant,
									MATERIAL_REQUISITION_PURPOSE[2].constant
								].includes(item.purpose) &&
								item.requisitionType === MATERIAL_REQUISITION_TYPE[0].constant
									? 'Add Material'
									: 'Update Cost'}
							</p>
							<CommonImage src="/icons/right-arrow.svg" width={18} height={18} />
						</button>
					)}
				{isMaterialTeam() &&
					((item.status === 'REQUESTED' &&
						item.requisitionType === MATERIAL_REQUISITION_TYPE[1].constant) ||
						item.status === 'INVENTORY_ADDED') && (
						<button
							className="button text size32 pr-1 pl-0 gray_dark_02 d-flex align-items-center"
							onClick={async () => {
								await markAsCompleted(item.id);
							}}
						>
							<p className="regular-14 blue text-underline">Mark as Completed</p>
							<CommonImage src="/icons/right-arrow.svg" width={18} height={18} />
						</button>
					)}
				{item?.isFreeOfCost !== true && isMaterialTeam() && item.status === 'MATERIAL_ADDED' && (
					<button
						className="button text size32 pr-1 pl-0 gray_dark_02 d-flex align-items-center"
						onClick={async () => {
							await approveRequisition(item.id);
						}}
					>
						<p className="regular-14 blue text-underline">Send to inventory</p>
						<CommonImage src="/icons/right-arrow.svg" width={18} height={18} />
					</button>
				)}
				{isStoreManager() && item.status === 'APPROVED' && (
					<button
						className="button text size32 pr-1 pl-0 gray_dark_02 d-flex align-items-center"
						onClick={() => {
							setSelectedRequisition(item);
							toggleUpdateModal();
						}}
					>
						<p className="regular-14 blue text-underline">Update Inventory</p>
						<CommonImage src="/icons/right-arrow.svg" width={18} height={18} />
					</button>
				)}
			</>
		);
	};

	const renderTableData = () => {
		const renderStatus = (status) => {
			let statusColor = ['REJECTED'].includes(status)
				? 'red-1'
				: ['REQUESTED', 'MATERIAL_ADDED', 'SENT_FOR_APPROVAL'].includes(status)
					? 'yellow-1'
					: 'green-1';
			return (
				<span className={`status ${statusColor}`}>
					{capitalizeFirstLetter(status.replaceAll('_', ' '))}
				</span>
			);
		};

		const onAddToClipBoard = (text) => {
			navigator.clipboard
				.writeText(text)
				.then(() => {
					toastSuccess('Copied successfully');
				})
				.catch(() => {
					toastError('Something went wrong!');
				});
		};

		const renderFabricConstructions = (fabricConstructionGroupNameQtyMap = {}) => {
			return Object.entries(fabricConstructionGroupNameQtyMap).map(([type, qty]) => {
				return (
					<p key={type} className="regular-14 gray_dark_02">
						{capitalizeFirstLetter(type.replaceAll('_', ' '))}
						{qty !== -1 && (
							<>
								: {qty}{' '}
								{['SWATCH', 'TRIMS', 'ACCESSORIES'].includes(tabState.activeTab)
									? 'Pcs'
									: type === 'KNIT'
										? 'Kg'
										: 'Yard'}
							</>
						)}
					</p>
				);
			});
		};

		return materials?.data?.map((item) => {
			return (
				<tr key={`material_${item.id}`}>
					<td className="requisition-details-column">
						<p className="regular-12 weight-600 gray_dark_01 d-flex align-items-center">
							{item.referenceId}
							<span
								className="copy-requisition ml-2 cursor-pointer"
								onClick={() => onAddToClipBoard(item.referenceId)}
							>
								<CommonImage src="/icons/copy-gray.svg" width={20} height={20} alt="copy" />
							</span>
						</p>
						<p className="regular-14 gray_dark_02">{item.createdBy}</p>
						<p className="regular-14 gray_dark_02">
							{changeDateFormat(item.createdAt, 'YYYY-MM-DD', 'DD MMM, YYYY')}
						</p>
					</td>
					<td className="brands-column">
						<p className="regular-12 weight-600 gray_dark_01">
							{capitalizeFirstLetter(item.purpose.replaceAll('_', ' '))}
						</p>
						<p className="regular-14 gray_dark_02">{item?.brandResponse?.name}</p>
					</td>
					<td className="required-within-column">
						<p className="regular-14 gray_dark_02">
							{changeDateFormat(item.requiredWithinDate, 'YYYY-MM-DD', 'DD MMM, YYYY')}
						</p>
					</td>
					{['FABRIC', 'SWATCH'].includes(tabState.activeTab) && (
						<td className="fabric-type-column">
							{renderFabricConstructions(item?.fabricConstructionGroupNameQtyMap)}
						</td>
					)}
					{['TRIMS', 'ACCESSORIES'].includes(tabState.activeTab) && (
						<td className="fabric-type-column">
							<p className="regular-14 gray_dark_02">
								{tabState.activeTab === 'TRIMS'
									? renderFabricConstructions(item?.trimsCategoryQtyMap)
									: renderFabricConstructions(item?.brandingCategoryQtyMap)}
							</p>
						</td>
					)}
					<td className="colors-column">{renderColorsColumn(item?.colorResponseList)}</td>
					<td className="status-column">
						<div className="d-flex flex-column fjustify-content-start align-items-start gap-2">
							{renderStatus(item.status)}
							{renderStatusWiseActions(item)}
						</div>
						{item?.approverResponseList?.length > 0 &&
							['SENT_FOR_APPROVAL', 'DISBURSEMENT_PENDING'].includes(item.status) && (
								<div className="approve-btn" onClick={() => setSelectedApproval(item.id)}>
									<button className="button text size32 pr-0 pl-0 gray_dark_02">
										<p className="regular-14 blue text-underline">Approvers</p>
									</button>
									<CommonImage src="/icons/right-arrow.svg" width={18} height={18} />
								</div>
							)}
						{selectedApproval === item.id && (
							<div ref={approvalRef} className="design-timeline-container">
								<ul className="design-timeline design-timeline-centered">
									{item?.approverResponseList?.map((approver) => (
										<li
											key={`approver_${approver.approverResponse.id}`}
											className={`design-timeline-item status ${approver.isApproved ? 'purple-1' : ''}`}
										>
											<div className="design-timeline-marker"></div>
											<div className="design-timeline-content">
												<p className="regular-14">{approver.approverResponse.name}</p>
												{/*<span className="regular-12">19th Nov, 2024</span>*/}
											</div>
										</li>
									))}
								</ul>
							</div>
						)}
					</td>
					<td className="remarks-column">
						<p className="regular-14 gray_dark_02">{item?.details}</p>
					</td>
					<td className="attachments-column">
						{item.purpose !== MATERIAL_REQUISITION_PURPOSE[1].constant && renderRangePlan(item)}
						{renderDocuments(item)}
					</td>
					<td className="action-column">
						{item.status !== 'REJECTED' && (
							<div className="option">
								<div className="dropdown">
									<button
										className="btn btn-default dropdown-toggle"
										type="button"
										id="menu1"
										data-toggle="dropdown"
										aria-expanded="false"
									>
										<CommonImage src="/icons/more_vertical.svg" />
									</button>

									<ul
										className="dropdown-menu dropdown-menu-right shadow-lg"
										role="menu"
										aria-labelledby="menu1"
										x-placement="bottom-end"
									>
										{renderActionButton(item)}
									</ul>
								</div>
							</div>
						)}
					</td>
				</tr>
			);
		});
	};

	return (
		<LoaderComponent
			loading={
				isLoading || isMarkAsCompletedLoading || isForceApproveLoading || isFreeRequisitionLoading
			}
		>
			<div className="top-header">
				<div className="page-title-area pb-2 d-flex align-items-center">
					<h1 className="semibold-16">Material Requisition</h1>
				</div>
				<div className="navigation-tab bg-white">
					<ul>
						{MATERIAL_REQUISITION_TYPE?.map((item) => (
							<li
								className={tabState.activeTab === item.constant ? 'active' : ''}
								key={item.constant}
								onClick={() => handleTabChange(item.constant)}
							>
								{item.name}
							</li>
						))}
					</ul>
				</div>
				<div className="filter-container-top block-library-filter-top d-block pt-2 pb-2">
					<div className="d-flex align-items-center justify-content-between">
						<div className="filters">
							<div className="filter-toggle" onClick={() => setIsShowFilter(!isShowFilter)}>
								<CommonImage src="/icons/filter24.svg" alt="filter" />
								<span>Filter</span>
							</div>
							<MaterialRequisitionSummary
								materialType={tabState.activeTab}
								totalItems={materials?.totalElements}
							/>
							<Chip />
						</div>
						<div className="filter-actions sample-dashboard-filter">
							<Search placeholder="reference no" />
							{isShowMaterialRequisitionBtn() && (
								<div className="sort-button design-view-sort mt-1">
									<button className="button size32" onClick={toggleRequestTypeModal}>
										Request Material
									</button>
								</div>
							)}
						</div>
					</div>
				</div>
			</div>
			<section className="filter-data-container filter-v3-data-container under-inner-tabs bg-white">
				<FilterComponentV2
					filterPageName={allFilterPages.MATERIAL_REQUISITION}
					fields={
						['FABRIC', 'SWATCH'].includes(tabState?.activeTab)
							? materialRequisitionFilterFieldList
							: materialRequisitionTrimsAndAccessoriesFilterFieldList
					}
					showFilter={isShowFilter}
					isDependentFilter={true}
				/>
				<div className={`data-table scroll-x-label ${isShowFilter ? 'expand' : ''}`}>
					<div className="material-requisition-list-table">
						<table className="table requisition-table with-border height-sm hover">
							<thead>
								<tr>
									<th>Requisition details</th>
									<th>Brands</th>
									<th>Required within</th>
									{['FABRIC', 'SWATCH'].includes(tabState.activeTab) && (
										<th>Fabric type</th>
									)}
									{['TRIMS', 'ACCESSORIES'].includes(tabState.activeTab) && (
										<th>{capitalizeFirstLetter(tabState.activeTab)} Category</th>
									)}
									<th>Colors</th>
									<th>Status</th>
									<th>Remarks</th>
									<th>Attachments</th>
									<th>Action</th>
								</tr>
							</thead>
							<tbody>{renderTableData()}</tbody>
						</table>
					</div>
				</div>
			</section>

			{isShowMaterialRequestTypeModal && (
				<MaterialRequisitionModalWrapper onCloseModal={toggleRequestTypeModal} />
			)}
			{isShowRejectionModal && (
				<MaterialRejectionModal
					onCloseModal={toggleRejectionModal}
					selectedRequisition={selectedRequisition}
				/>
			)}
			{isShowViewModal && (
				<ViewMaterialRequisition
					onCloseModal={() => {
						toggleViewModal();
						setSelectedRequisition(null);
					}}
					selectedRequisition={selectedRequisition}
					setSelectedRequisition={setSelectedRequisition}
				/>
			)}
			{isShowUpdateInventory && (
				<UpdateInventory
					onCloseModal={() => {
						toggleUpdateModal();
						setSelectedRequisition(null);
					}}
					selectedRequisition={selectedRequisition}
					callBack={getMaterialRequisitionList}
				/>
			)}
			{isShowInventoryModal && (
				<ViewInventory
					onCloseModal={() => {
						toggleInventoryModal();
						setSelectedRequisition(null);
					}}
					selectedRequisition={selectedRequisition}
				/>
			)}
		</LoaderComponent>
	);
};

export default MaterialRequisition;
